/**
 * Volver adonde se iba después de iniciar sesión.
 *
 * El aviso de una alarma abre `/monitor/alarmas/12`; sin sesión, `RequireAuth`
 * manda a `/login`, y tras Google el puente aterrizaba siempre en `/`. Ahora
 * `RequireAuth` guarda la ruta pedida en `sessionStorage` y el login (si ya hay
 * sesión) o el puente (al terminar) la leen UNA vez y la borran.
 *
 * Lo guardado se trata como no fiable: pasa por el mismo patrón que los enlaces
 * de las alarmas (`isInternalLink`), así que nunca lleva a otro host, y nunca
 * vuelve a `/login` ni a `/session-bridge` (sería un bucle).
 */
import { isInternalLink } from '@/lib/internalLink';

export const RETURN_TO_KEY = 'bp_admin_return_to';

const AUTH_PATHS = ['/login', '/session-bridge'];

function isAuthPath(path: string): boolean {
  const pathname = (path.split(/[?#]/, 1)[0] ?? '').replace(/\/+$/, '');
  return AUTH_PATHS.includes(pathname);
}

/** La ruta si es interna y no es de login; si no, `/`. */
export function safeReturnTo(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '/';
  // Un tabulador o un salto de línea dentro de `//` hacen que el navegador lo
  // lea como otro host: se quitan antes de mirar nada.
  const value = raw.replace(/[\t\r\n]/g, '');
  if (!isInternalLink(value) || isAuthPath(value)) return '/';
  return value;
}

/** Guarda la ruta pedida (sin sesión todavía). Nunca lanza. */
export function rememberReturnTo(path: string): void {
  if (safeReturnTo(path) === '/') return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    /* almacenamiento bloqueado: se vuelve a `/` */
  }
}

/** Lee y BORRA la ruta guardada; `/` si no hay o no vale. Nunca lanza. */
export function takeReturnTo(): string {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* almacenamiento bloqueado */
  }
  return safeReturnTo(raw);
}
